'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { areRenderPropsValueEqual } from '../_pure/valueEquality.js';
import { useGpuResources } from '../useGpuResources.js';
import { GappedConnectorLine } from './GappedConnectorLine.js';
import { NearEyeClippedConnectorLine } from './NearEyeClippedConnectorLine.js';
import { ProgressivePathConnectorLine } from './ProgressivePathConnectorLine.js';
import { RippleConnectorLine } from './RippleConnectorLine.js';
import { OrbitingConnectorLine } from './OrbitingConnectorLine.js';
const ThickConnectorTube = memo(function ThickConnectorTube(props) {
    const { cache, start, end, thickness, color, opacity, depthTest, strokeCount = 1, strokeSpacing = thickness * 1.5, innerStrokePattern, } = props;
    const segments = useMemo(() => {
        const from = new THREE.Vector3(...start);
        const to = new THREE.Vector3(...end);
        const direction = to.clone().sub(from);
        const length = direction.length();
        if (length <= 1e-8)
            return [];
        direction.normalize();
        const reference = Math.abs(direction.x) <= Math.abs(direction.y)
            && Math.abs(direction.x) <= Math.abs(direction.z)
            ? new THREE.Vector3(1, 0, 0)
            : Math.abs(direction.y) <= Math.abs(direction.z)
                ? new THREE.Vector3(0, 1, 0)
                : new THREE.Vector3(0, 0, 1);
        const perpendicular = direction.clone().cross(reference).normalize();
        const output = [];
        for (let stroke = 0; stroke < strokeCount; stroke += 1) {
            const offset = (stroke - (strokeCount - 1) / 2) * strokeSpacing;
            const offsetVector = perpendicular.clone().multiplyScalar(offset);
            output.push({
                start: from.clone().add(offsetVector).toArray(),
                end: to.clone().add(offsetVector).toArray(),
                thickness,
            });
        }
        if (innerStrokePattern === 'dashed') {
            const innerStrokeCount = Math.max(1, strokeCount - 1);
            const dashIntervals = [[0, 0.28], [0.4, 0.68], [0.8, 1]];
            for (let stroke = 0; stroke < innerStrokeCount; stroke += 1) {
                const offset = strokeCount === 1
                    ? strokeSpacing / 2
                    : (stroke - (strokeCount - 2) / 2) * strokeSpacing;
                const offsetVector = perpendicular.clone().multiplyScalar(offset);
                for (const [fromFraction, toFraction] of dashIntervals) {
                    output.push({
                        start: from.clone().addScaledVector(direction, length * fromFraction)
                            .add(offsetVector).toArray(),
                        end: from.clone().addScaledVector(direction, length * toFraction)
                            .add(offsetVector).toArray(),
                        thickness: thickness * 0.28,
                    });
                }
            }
        }
        return output;
    }, [start, end, strokeCount, strokeSpacing, innerStrokePattern, thickness]);
    return _jsx("group", { children: segments.map((segment, index) => (_jsx(ThickConnectorSegment, { cache: cache, ...segment, color: color, opacity: opacity, depthTest: depthTest }, index))) });
}, areRenderPropsValueEqual);
const CONNECTOR_TUBE_GEOMETRY_SPEC = { geometryKind: 'connector-tube', radialSegments: 8 };
const ThickConnectorSegment = memo(function ThickConnectorSegment(props) {
    const { cache, start, end, thickness, color, opacity, depthTest } = props;
    const transform = useMemo(() => {
        const from = new THREE.Vector3(...start);
        const to = new THREE.Vector3(...end);
        const direction = to.clone().sub(from);
        const length = direction.length();
        if (length <= 1e-8)
            return null;
        return {
            position: from.add(to).multiplyScalar(0.5).toArray(),
            quaternion: new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize()).toArray(),
            length,
        };
    }, [start, end]);
    const geometry = cache.getGeometry(CONNECTOR_TUBE_GEOMETRY_SPEC);
    const material = cache.getMaterial({ materialKind: 'connector-tube', color, opacity, depthTest });
    useGpuResources(cache, [geometry, material]);
    return transform
        ? _jsx("mesh", { position: transform.position, quaternion: transform.quaternion, scale: [thickness, transform.length, thickness], geometry: geometry, material: material })
        : null;
}, areRenderPropsValueEqual);
const ConnectorLine = memo(function ConnectorLine(props) {
    const { start, end, points, color, opacity, depthTest = true } = props;
    const geometry = useMemo(() => {
        const out = new THREE.BufferGeometry();
        const vertices = (points ?? [start, end]).map((point) => new THREE.Vector3(...point));
        const segments = [];
        for (let index = 1; index < vertices.length; index += 1) {
            const previous = vertices[index - 1];
            const current = vertices[index];
            if (previous && current)
                segments.push(previous, current);
        }
        out.setFromPoints(segments);
        return out;
    }, [start, end, points]);
    const material = useMemo(() => {
        return new THREE.LineBasicMaterial({
            color,
            transparent: opacity < 1,
            opacity,
            depthTest,
        });
    }, [color, opacity, depthTest]);
    useEffect(() => {
        return () => {
            geometry.dispose();
            material.dispose();
        };
    }, [geometry, material]);
    return _jsx("lineSegments", { geometry: geometry, material: material });
}, areRenderPropsValueEqual);
const CurvedConnectorLine = memo(function CurvedConnectorLine(props) {
    const { start, control, end, color, opacity, depthTest = true } = props;
    const geometry = useMemo(() => {
        const out = new THREE.BufferGeometry();
        const curve = new THREE.QuadraticBezierCurve3(new THREE.Vector3(...start), new THREE.Vector3(...control), new THREE.Vector3(...end));
        const curvePoints = curve.getPoints(24);
        const segmentPoints = [];
        for (let i = 0; i < curvePoints.length - 1; i += 1) {
            const segmentStart = curvePoints[i];
            const segmentEnd = curvePoints[i + 1];
            if (!segmentStart || !segmentEnd)
                continue;
            segmentPoints.push(segmentStart, segmentEnd);
        }
        out.setFromPoints(segmentPoints);
        return out;
    }, [start, control, end]);
    const material = useMemo(() => {
        return new THREE.LineBasicMaterial({
            color,
            transparent: opacity < 1,
            opacity,
            depthTest,
        });
    }, [color, opacity, depthTest]);
    useEffect(() => {
        return () => {
            geometry.dispose();
            material.dispose();
        };
    }, [geometry, material]);
    return _jsx("lineSegments", { geometry: geometry, material: material });
}, areRenderPropsValueEqual);
function renderConnectorItem({ itemKey, presentation: plan }, resourceCache, readCueElapsed) {
    switch (plan.kind) {
        case 'line':
            return _jsx(ConnectorLine, { ...plan }, itemKey);
        case 'tube':
            return _jsx(ThickConnectorTube, { cache: resourceCache, ...plan }, itemKey);
        case 'orbit':
            return _jsx(OrbitingConnectorLine, { ...plan, ...(readCueElapsed ? { readCueElapsed } : {}) }, itemKey);
        case 'curve':
            return _jsx(CurvedConnectorLine, { ...plan }, itemKey);
        case 'activation':
            return _jsxs("group", { children: [_jsx(ConnectorLine, { start: plan.start, end: plan.end, color: plan.color, opacity: plan.opacity }), plan.overlay && _jsx(ConnectorLine, { ...plan.overlay })] }, itemKey);
        case 'gapped':
            return _jsx(GappedConnectorLine, { ...plan }, itemKey);
        case 'ripple':
            return _jsx(RippleConnectorLine, { ...plan }, itemKey);
        case 'near-eye-clipped':
            return _jsx(NearEyeClippedConnectorLine, { ...plan }, itemKey);
        case 'progressive-path':
            return _jsx(ProgressivePathConnectorLine, { ...plan }, itemKey);
    }
}
export function ConnectorConstellationRenderer({ plan, resourceCache, readCueElapsed }) {
    return plan === null ? null : _jsx("group", { children: plan.items.map(item => renderConnectorItem(item, resourceCache, readCueElapsed)) }, plan.constellationKey);
}
